import './lib/zod-jitless';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { ToastProvider, getGlobalToast, Button, Flex, Text, Heading } from '@chatsdet/ui';
import * as Sentry from '@sentry/react';
import { router } from './router.js';
import { installFetchInterceptor } from './lib/fetch-interceptor';
import { ApiError } from './lib/api-error';
import { shouldSuppressMutationSentry, shouldSuppressQuerySentry } from './lib/sentry-error-filter';
import { beforeSend } from './lib/sentry-filters';
import '@chatsdet/ui/styles.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './styles.css';

installFetchInterceptor();

// Real deployment stage (dev / production) injected at build time by SST.
// Falls back to Vite's MODE for local development where VITE_APP_ENV is unset.
const SENTRY_ENV = import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE;
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE || undefined;

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: SENTRY_ENV,
    release: SENTRY_RELEASE,
    // Sentry v11's default dataCollection policy preserves the v10 behavior
    // when sendDefaultPii was explicitly enabled.
    // Keep legacy span lifecycle behavior: existing scope tags and extra data
    // are attached to transactions, but v11 streamed spans do not carry them.
    traceLifecycle: 'static',
    attachStacktrace: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserSessionIntegration({ lifecycle: 'route' }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: SENTRY_ENV === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend,
  });
}

function ErrorFallback() {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="lg"
      className="min-h-screen bg-background p-8"
    >
      <Heading level={1}>Something went wrong</Heading>
      <Text className="text-muted-foreground text-center max-w-md">
        An unexpected error occurred. Please try refreshing the page.
      </Text>
      <Button variant="primary" onPress={() => window.location.reload()}>
        Refresh page
      </Button>
    </Flex>
  );
}

function handleToastError(message: string) {
  Sentry.captureMessage(message, {
    level: 'error',
    tags: { source: 'toast' },
  });
}

const root = createRoot(document.getElementById('root')!);
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Skip explicit user errors and any expected client-side failures (4xx).
      // 404 from "switching orgs" / missing entities and 401/403 from permissions
      // are not bugs — they're outcomes the UI is supposed to render.
      if (shouldSuppressQuerySentry(error)) {
        return;
      }

      // Group by the query's first key segment (e.g. "workflow-runs"). Without
      // this every queryFn collapses into one Sentry issue because the minified
      // bundle exposes a single `queryFn` symbol across all callsites.
      const queryName =
        Array.isArray(query.queryKey) && typeof query.queryKey[0] === 'string'
          ? query.queryKey[0]
          : 'unknown-query';

      Sentry.withScope((scope) => {
        scope.setFingerprint(['react-query', queryName]);
        scope.setTag('source', 'query');
        scope.setTag('query.name', queryName);
        if (error instanceof ApiError) {
          scope.setTag('http.status_code', String(error.status));
        }
        scope.setContext('reactQuery', {
          queryKey: JSON.stringify(query.queryKey),
          queryName,
        });
        Sentry.captureException(error);
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const isUserError = error instanceof ApiError && error.userError;

      if (isUserError && !mutation.meta?.suppressToast) {
        getGlobalToast()?.error(error.message, { reportError: false });
      }

      if (shouldSuppressMutationSentry(error)) return;

      const mutationName =
        Array.isArray(mutation.options.mutationKey) &&
        typeof mutation.options.mutationKey[0] === 'string'
          ? mutation.options.mutationKey[0]
          : 'unknown-mutation';

      Sentry.withScope((scope) => {
        scope.setFingerprint(['react-query-mutation', mutationName]);
        scope.setTag('source', 'mutation');
        scope.setTag('mutation.name', mutationName);
        if (error instanceof ApiError) {
          scope.setTag('http.status_code', String(error.status));
        }
        scope.setContext('reactQuery', {
          mutationKey: mutation.options.mutationKey
            ? JSON.stringify(mutation.options.mutationKey)
            : undefined,
          mutationName,
        });
        Sentry.captureException(error);
      });
    },
  }),
});
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={<ErrorFallback />}
    >
      <QueryClientProvider client={queryClient}>
        <ToastProvider onError={handleToastError}>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
